<template>
  <div class="cf-app-layout">
    <div class="cf-app-layout__header">
      <!-- App Header -->
      <cf-app-header :logo-navigation="appLogoRouteConfig">
        <template #header-left-slot>
          <cf-drop-down-menu-button />
        </template>

        <template
          v-if="isAccountManagementEnabled || isCartEnabled"
          #header-right-slot
        >
          <cf-account-button v-if="isAccountManagementEnabled && !onAccountPage" />

          <cf-cart-button v-if="isCartEnabled" />
        </template>
      </cf-app-header>
    </div>

    <!-- App Content -->
    <div class="cf-app-layout__app-wrapper">
      <div
        v-if="isLoading"
        :class="['cf-container-full-width', { 'cf-page-loader': isLoading }]"
      >
        <div class="cf-loader" />
      </div>

      <template v-else>
        <transition
          v-if="routerViewTransition.includeTransition"
          :name="routerViewTransition.name"
          mode="out-in"
        >
          <router-view />
        </transition>

        <router-view v-else />
      </template>
    </div>

    <!-- App Footer -->
    <div class="cf-app-layout__footer">
      <cf-app-footer />
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';

  import applicationFlows from '@/constants/applicationFlows';

  import orderAheadInitializer from '@/mixins/order-ahead-initializer';
  import merchantInitializer from '@/mixins/merchant-initializer';

  import CfAppHeader from '@/view/components/globals/cf-app-header/index.vue';
  import CfAppFooter from '@/view/components/globals/cf-app-footer/index.vue';
  import CfDropDownMenuButton from '@/view/components/globals/cf-app-header/cf-drop-down-menu-button/index.vue';
  import CfAccountButton from '@/view/components/globals/cf-app-header/cf-account-button/index.vue';
  import CfCartButton from '@/view/components/globals/cf-app-header/cf-cart-button/index.vue';

  export default {
    name: 'MerchantLayout',

    metaInfo() {
      const { merchant } = this;

      return {
        titleTemplate(titleChunk) {
          return titleChunk ? `${titleChunk} | Order | ${merchant.name}` : `Order | ${merchant.name}`;
        }
      };
    },

    components: {
      CfAppHeader,
      CfAppFooter,
      CfDropDownMenuButton,
      CfAccountButton,
      CfCartButton
    },

    mixins: [orderAheadInitializer, merchantInitializer],

    props: {
      routerViewTransition: {
        type: Object,
        default() {
          return {
            includeTransition: false,
            name: null
          };
        }
      }
    },

    computed: {
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('session', ['applicationFlow']),

      appLogoRouteConfig() {
        // Check if current route has custom header logo route
        const customRoute = this.$route.meta?.headerLogoRoute;

        return {
          isClickable: true,
          route: customRoute || 'orderMenu' // Default to orderMenu
        };
      },

      isLoading() {
        return this.applicationFlow === applicationFlows.PAY_AT_TABLE
          ? this.isMerchantLoading
          : this.isOrderAheadLoading;
      },

      isAccountManagementEnabled() {
        return this.merchant.options.signUpEnabled;
      },

      isCartEnabled() {
        return !this.merchant.features['disable-ordering'];
      },

      onAccountPage() {
        return this.$route.name === 'myAccount';
      }
    },

    created() {
      if (this.applicationFlow === applicationFlows.PAY_AT_TABLE) {
        this.initializeMerchant({
          defaultFulfillmentType: 'standard',
          defaultOrderType: 'pay',
          orderTypes: ['pay', 'table']
        });
      }
      else {
        this.initializeOrderAhead();
      }
    },

    unmounted() {
      if (this.applicationFlow === applicationFlows.PAY_AT_TABLE) {
        this.destroyedMerchantHandler();
      }
      else {
        this.destroyedOrderAheadHandler();
      }
    }
  };
</script>
